import * as Sentry from "@sentry/react";
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';
import { UserProvider } from './contexts/UserContext';
import App from './App';
import './index.css';

const ErrorFallback = ({ error }: { error: Error | null }) => {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="text-center">
        <h1 className="mb-4 text-2xl font-bold text-red-600">Something went wrong</h1>
        <p className="mb-4 text-gray-600">We're sorry for the inconvenience. Please try refreshing the page.</p>
        {error && (
          <pre className="max-w-xl overflow-auto rounded bg-gray-100 p-4 text-sm">
            {error.message}
          </pre>
        )}
      </div>
    </div>
  );
};

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Failed to find the root element');

// Initialize Sentry
Sentry.init({
  dsn: "https://e940bbc32c99ef3e2ec462a8bbd085a2@o4508597323956224.ingest.us.sentry.io/4508597355216896",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracePropagationTargets: [
    "localhost:5173", // Frontend dev server
    "localhost:5001", // Backend dev server
    /^https:\/\/creator-management-production\.up\.railway\.app/, // Production backend
    /^https:\/\/.*\.creatorform\.app/, // All subdomains of .app
    /^https:\/\/creatorform\.app/, // Main .app domain
    /^https:\/\/.*\.creatorform\.co/, // All subdomains of .co
    /^https:\/\/creatorform\.co/ // Main .co domain
  ],
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  defaultTags: {
    environment: process.env.NODE_ENV,
    form_type: 'creator_application'
  }
});

const root = createRoot(rootElement);

root.render(
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <StrictMode>
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: import.meta.env.VITE_AUTH0_CALLBACK_URL,
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        }}
        useRefreshTokens={true}
        cacheLocation="localstorage"
      >
        <UserProvider>
          <App />
        </UserProvider>
      </Auth0Provider>
    </StrictMode>
  </Sentry.ErrorBoundary>
);
